import { useState } from 'react';
import { Check, PenLine, Send, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/ui/button';
import type { DecisionKind } from '@/types';

/**
 * The one way out of the review: Send.
 *
 * There is no second status to pick here. Every comment carries its own, and
 * what is sent is what the comments add up to - the most serious one wins, so
 * a single "Reject" comment rejects and "Looks good" alone approves. To send,
 * there has to be at least one comment: approving is a "Looks good" comment,
 * the same as asking for a change. Who is deciding comes from the details at
 * the top of the panel.
 */
const OUTCOMES: Record<
  DecisionKind,
  { label: string; verb: string; hint: string; icon: typeof Check; badge: string; solid: string }
> = {
  approve: {
    label: 'Approve',
    verb: 'Send approval',
    hint: 'The lab mills it as it is.',
    icon: Check,
    badge: 'bg-success-50 text-success-600',
    solid: 'bg-success-500 hover:bg-success-600 text-white',
  },
  changes_requested: {
    label: 'Changes',
    verb: 'Send changes',
    hint: 'The lab revises it and sends you a new version.',
    icon: PenLine,
    badge: 'bg-warning-50 text-warning-600',
    solid: 'bg-warning-500 hover:bg-warning-600 text-white',
  },
  reject: {
    label: 'Reject',
    verb: 'Send rejection',
    hint: 'The design is not usable and is started again.',
    icon: X,
    badge: 'bg-danger-50 text-danger-600',
    solid: 'bg-danger-500 hover:bg-danger-600 text-white',
  },
};

interface SendDockProps {
  /** The status the comments add up to (null with none). */
  fromComments: DecisionKind | null;
  commentCount: number;
  /** Why sending is not possible yet (e.g. no name), or null. */
  blocked: string | null;
  onSend: (decision: DecisionKind) => Promise<void>;
}

export function SendDock({ fromComments, commentCount, blocked, onSend }: SendDockProps) {
  const [sending, setSending] = useState(false);
  const outcome = fromComments ? OUTCOMES[fromComments] : null;
  const missing =
    blocked ??
    (commentCount === 0 || !fromComments
      ? 'Add a comment above - "Looks good" approves the design.'
      : null);
  const label = outcome
    ? `${outcome.verb} with ${String(commentCount)} comment${commentCount === 1 ? '' : 's'}`
    : 'Send to the lab';

  const send = async () => {
    if (!fromComments || missing || sending) return;
    setSending(true);
    try {
      await onSend(fromComments);
    } finally {
      setSending(false);
    }
  };

  const Icon = outcome?.icon;
  return (
    <div className="relative z-10 shrink-0 border-t border-border bg-background p-4 shadow-[0_-8px_20px_-16px_rgba(15,23,42,0.35)]">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-[13px] font-semibold">Status</h2>
        {outcome && Icon ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded px-1.5 py-px text-[11px] font-semibold tracking-wide uppercase',
              outcome.badge,
            )}
          >
            <Icon className="size-3" />
            {outcome.label}
          </span>
        ) : (
          <span className="rounded bg-muted px-1.5 py-px text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
            None yet
          </span>
        )}
        <span className="ml-auto truncate text-[11.5px] text-muted-foreground">
          {outcome ? outcome.hint : 'Set by your comments'}
        </span>
      </div>
      <Button
        onClick={() => void send()}
        disabled={Boolean(missing) || sending}
        className={cn('w-full', outcome?.solid)}
      >
        <Send />
        {sending ? 'Sending…' : label}
      </Button>
      {missing && !sending && (
        <p className="mt-1.5 text-center text-[11.5px] font-medium text-warning-600">{missing}</p>
      )}
      <p className="mt-1 text-center text-[11px] text-muted-foreground">
        Sent to the technician with your name and clinic. It cannot be changed from this link.
      </p>
    </div>
  );
}
